import { Button, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { requestErisData } from '~/api/projects2';
import { useOwnMapClicks } from '~/components2/Map/mapClickOwnership';
import { measureStatus } from '~/components2/Map/Widgets/Measure/MeasureSurface';
import { Notify } from '~/components2/Notify';
import { GlobalFilter, Pagination, TableV2 } from '~/components2/Table';
import type { ProjectDetailsParams } from '..';
import { columns, columnVisibility } from './columns';
import { downloadFullPDF } from './downloadFullPDF';
import StudyPopup from './StudyPopup';
import store from './store';

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { environmentalStudyData, selectedObjectId, isLoading, hasErisData, error } = useSnapshot(
    store,
  ) as typeof store;
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const onDownloadFullPdf = async () => {
    setIsDownloadingPdf(true);
    try {
      await downloadFullPDF(projectId);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  useEffect(() => {
    store.fetchEnvironmentalStudyData(projectId);
    return () => store.reset();
  }, [projectId]);

  const handleRequestErisData = async () => {
    try {
      await requestErisData(projectId);
      Notify.info({
        title: 'ERIS Data Request',
        message: 'Your request for ERIS data has been sent to our support team. We will contact you shortly.',
      });
    } catch (error) {
      console.error('Error requesting ERIS data:', error);
      Notify.error({
        title: 'ERIS Data Request',
        message: 'Failed to send ERIS data request. Please try again or contact support directly.',
      });
    }
  };

  const propertyFeatures = useMemo(() => {
    if (!environmentalStudyData) return [];
    const features = environmentalStudyData.features.map((feature) => feature.properties);
    return features;
  }, [environmentalStudyData]);

  const table = useReactTable({
    data: propertyFeatures,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    enableRowSelection: true,
    columnResizeMode: 'onChange',
    getRowId: (row) => row!.id?.toString() || '',
    state: { columnVisibility },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 100 },
      sorting: [{ id: 'mapKey', desc: false }],
    },
  });

  const onRowClick = (row: any) => {
    const x = parseFloat(row.original.x);
    const y = parseFloat(row.original.y);
    if (!Number.isNaN(x) && !Number.isNaN(y)) {
      store.setClickedCoordinates({ lat: y, lng: x });

      const feature: GeoJSON.Feature<GeoJSON.Point> = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [x, y],
        },
        properties: row.original,
      };

      store.setSelectedFeatures([feature]);
      store.setPopupLocation([x, y]);
    }
  };

  const getRowClassName = (row: any) => {
    return row.getIsSelected() ? '!bg-blue-50 !bg-secondary-light/0' : '';
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (selectedObjectId) {
      const row = table.getRowModel().rowsById[selectedObjectId];
      if (row) {
        table.setRowSelection({ [selectedObjectId]: true });
        const sortedRows = table.getSortedRowModel().rows;
        const currentIndex = sortedRows.findIndex((r) => r.id.toString() === selectedObjectId);

        if (currentIndex !== -1) {
          const pageIndex = Math.floor(currentIndex / table.getState().pagination.pageSize);

          table.setPageIndex(pageIndex);

          timer = setTimeout(() => {
            const rowElement = document.getElementById(`row-${row.id}`);
            if (rowElement) {
              rowElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 0);
        }
      }
    } else {
      table.setRowSelection({});
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [selectedObjectId, table.getState().sorting]);

  if (isLoading) {
    return <SkeletonSpinnerCentered />;
  }

  if (!hasErisData) {
    return (
      <div className="relative flex h-full w-full flex-col justify-start space-y-4 overflow-hidden">
        <div className="grid grid-cols-1 overflow-auto">
          <div className="mb-4">
            <p className="mb-2 text-gray-700 text-sm">
              The{' '}
              <a
                href="https://www.erisinfo.com/database-reports/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-50 underline hover:text-blue-60"
              >
                ERIS Database Report
              </a>{' '}
              data is not currently available for this project. Click the button below to request this data from our
              team. We will contact you shortly.
            </p>
            <Button onClick={handleRequestErisData} data-testid="eris-order-button">
              Order ERIS Data Report
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative flex h-full w-full flex-col justify-start space-y-4 overflow-hidden">
        <div className="grid grid-cols-1 overflow-auto">
          <div className="mb-4">
            <p className="mb-2 text-red-600">{error}</p>
            <Button onClick={handleRequestErisData} data-testid="eris-order-button">
              Order ERIS Data Report
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-col justify-start space-y-4 overflow-hidden">
      <div className="grid grid-cols-1 overflow-auto">
        <div className="mb-4">
          <p className="mb-2 text-gray-700 text-sm">
            This tab provides access to ERIS Database Report results{' '}
            <b>within a 1/4 mile radius of the center of your project</b>. Environmental study data points are displayed
            in both map and tabular formats. Utilize the map key identifier to reference corresponding study details
            within the comprehensive PDF documentation.
          </p>
          <Button onClick={onDownloadFullPdf} loading={isDownloadingPdf} data-testid="eris-download-pdf-button">
            Download full PDF
          </Button>
        </div>
        <TableV2
          id="environmental-study-data"
          table={table}
          onRowClick={onRowClick}
          getRowClassName={getRowClassName}
          topSection={
            <div className="flex items-end justify-between pb-2">
              <div className="flex items-center space-x-4">
                <Pagination table={table} />
              </div>

              <div>
                <GlobalFilter table={table} />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}

export function MapChildren() {
  // This tab uses left-click to select environmental study points, so claim map-click ownership (CG-670 sidebar defers).
  useOwnMapClicks();
  const { environmentalStudyData, clickedCoordinates, selectedFeatures, popupLocation } = useSnapshot(
    store,
  ) as typeof store;

  const { current: map } = MapGL.useMap();

  async function onClick(e: MapGL.MapMouseEvent) {
    if (measureStatus.isMeasuring === true) return;

    if (map) {
      const foundFeatures = map?.queryRenderedFeatures(e.point, {
        layers: ['combinedenvironmentalStudyDataLayer'],
      }) as GeoJSON.Feature<GeoJSON.Point>[] | undefined;

      if (foundFeatures && foundFeatures.length > 0) {
        const feature = foundFeatures[0];
        const objectId = feature.properties?.id?.toString();
        if (objectId) {
          store.setSelectedObjectId(objectId);
        }
      }

      store.setSelectedFeatures(foundFeatures || []);
      store.setPopupLocation([e.lngLat.lng, e.lngLat.lat]);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (clickedCoordinates) {
      if (!map) return;
      map.flyTo({
        center: [clickedCoordinates.lng, clickedCoordinates.lat],
        zoom: 19,
      });
    }
  }, [clickedCoordinates]);

  const environmentalStudyDataLayer = useMemo(() => {
    if (!environmentalStudyData) return null;
    return (
      <MapGL.Source id="combinedenvironmentalStudyDataSource" type="geojson" data={environmentalStudyData}>
        <MapGL.Layer
          id="combinedenvironmentalStudyDataLayer"
          type="circle"
          paint={{
            'circle-radius': 3,
            'circle-color': 'blue',
          }}
        />
      </MapGL.Source>
    );
  }, [environmentalStudyData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!map) return;
    store.setCurrentMap(map);
    map.on('click', onClick);

    return () => {
      map?.off('click', onClick);
      store.resetCurrentMap();
    };
  }, [map]);

  const popup = useMemo(() => {
    if (selectedFeatures.length > 0 && popupLocation) {
      return (
        <StudyPopup
          popupLocation={popupLocation}
          selectedFeatures={selectedFeatures}
          setSelectedFeatures={(features) => store.setSelectedFeatures(features)}
          setPopupLocation={(location) => store.setPopupLocation(location)}
        />
      );
    }
  }, [selectedFeatures, popupLocation]);

  return (
    <>
      {environmentalStudyDataLayer}
      {popup}
    </>
  );
}
